/**
 * Sprint 23.6 — per-agent productivity hook.
 *
 * Wraps `get_agent_productivity` for the profile hero's Scorecard. Returns the
 * full result (current window + prior window + deltas). Defaults to a rolling
 * 7d window vs the 7d before that, computed server-side; callers can pass
 * explicit ISO timestamps to override.
 *
 * Cached for 60s (the scorecard changes on the order of completed tasks, not
 * seconds — and the hero already polls the live activity stream separately).
 * Refetches on window focus so the user sees fresh numbers when they return.
 */

import { useQuery } from "@tanstack/react-query";
import { mcpClient } from "../../lib/mcp-client";
import { useAuth } from "../../lib/hooks/use-auth";
import type { AgentProductivityResult } from "../../lib/types";

export function useAgentProductivity(
  agentId: string | null | undefined,
  opts?: { since?: string; compareSince?: string },
) {
  const { currentWorkspaceId } = useAuth();
  return useQuery<AgentProductivityResult>({
    queryKey: [
      "agent-productivity",
      currentWorkspaceId,
      agentId,
      opts?.since ?? "default",
      opts?.compareSince ?? "default",
    ],
    queryFn: () => mcpClient.getAgentProductivity(agentId as string, opts),
    enabled: !!currentWorkspaceId && !!agentId,
    staleTime: 60_000,
    refetchOnWindowFocus: true,
  });
}
